import { useNavigate } from 'react-router-dom';
import { usePaletteContext } from '../contexts/PaletteContext';
import { Palette, Camera, Plus } from 'lucide-react';

interface PalettePickerProps {
  prompt: string;
}

export function PalettePicker({ prompt }: PalettePickerProps) {
  const { palettes, currentPalette, setCurrentPalette } = usePaletteContext();
  const navigate = useNavigate();

  if (currentPalette) return null;

  if (palettes.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto p-6">
        <div style={{
          background: 'var(--panel, #fff)',
          border: '1px solid var(--line, #e5e7eb)',
          borderRadius: 'var(--radius-xl, 12px)',
          padding: '32px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'var(--accent-bg, oklch(0.95 0.03 260))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Palette size={24} style={{ color: 'var(--accent, oklch(0.55 0.15 260))' }} />
          </div>
          <p style={{ color: 'var(--ink-2, #6b7280)', fontSize: '14px', marginBottom: '20px' }}>{prompt}</p>
          <p style={{ color: 'var(--ink-3, #9ca3af)', fontSize: '13px', marginBottom: '24px' }}>
            No palettes yet. Get started by creating one:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxWidth: '280px', margin: '0 auto' }}>
            <button
              type="button"
              onClick={() => navigate('/palette')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '10px 18px', borderRadius: 'var(--radius, 6px)',
                background: 'var(--accent, oklch(0.55 0.15 260))',
                color: '#fff', fontWeight: 600, fontSize: '13px',
                border: 'none', cursor: 'pointer',
              }}
            >
              <Plus size={15} />
              Create a new palette
            </button>
            <button
              type="button"
              onClick={() => navigate('/extract')}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '10px 18px', borderRadius: 'var(--radius, 6px)',
                background: 'var(--panel-2, #f3f4f6)',
                color: 'var(--ink, #111)', fontWeight: 500, fontSize: '13px',
                border: '1px solid var(--line, #e5e7eb)', cursor: 'pointer',
              }}
            >
              <Camera size={15} />
              Extract from image
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div style={{
        background: 'var(--panel, #fff)',
        border: '1px solid var(--line, #e5e7eb)',
        borderRadius: 'var(--radius-xl, 12px)',
        padding: '24px',
      }}>
        <p style={{ color: 'var(--ink-2, #6b7280)', fontSize: '14px', marginBottom: '16px' }}>{prompt}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {palettes.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => setCurrentPalette(p)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '10px 12px', borderRadius: 'var(--radius, 6px)',
                border: '1px solid var(--line, #e5e7eb)',
                background: 'var(--panel, #fff)', cursor: 'pointer',
                textAlign: 'left', fontSize: '13px', color: 'var(--ink, #111)',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--panel-2, #f9fafb)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--panel, #fff)'; }}
            >
              <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                {p.colors.slice(0, 4).map((c, i) => (
                  <div
                    key={i}
                    style={{
                      width: 16, height: 16, borderRadius: '3px',
                      backgroundColor: c,
                    }}
                  />
                ))}
              </div>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
